import React, { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "react-query"
import { useNavigate, useParams } from "react-router-dom"

import * as Yup from "yup"
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Step, StepLabel, Stepper } from "@mui/material"
import { useTheme as useMuiTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import { Button, InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import moment from "moment"

import { KonanAPI } from "../../services/KonanAPI"
import type { WorkflowSimulationJobCreateRequest } from "../../types/custom/projects"
import type { WorkflowSimulationJobCreate } from "../../types/generated/api/WorkflowSimulationJobCreate"
import { KonanTimeHelper } from "../../utils/genericHelpers"
import type { SimulationCreationDialogProps } from "./Interfaces"
import { DataSource } from "./components/DataSource"

import styles from "./SimulationCreationDialog.module.scss"

const validationSchema = Yup.object({
  source: Yup.string().required(t("SimulationCreationDialog.validation.dataSourceRequired", { ns: "simulations" })),
  totalRequestsNumber: Yup.number(),
  datafile_columns: Yup.array().of(Yup.string()),
  predictions_datafile: Yup.string().when("source", {
    is: (source: string) => source !== "Live Data",
    then: (schema) =>
      schema.required(t("SimulationCreationDialog.validation.dataFileRequired", { ns: "simulations" })).min(1),
  }),
  target: Yup.string(),
  predictions_start_time: Yup.string().when("source", {
    is: "Live Data",
    then: (schema) =>
      schema.required(t("SimulationCreationDialog.validation.startTimeRequired", { ns: "simulations" })),
  }),
  predictions_end_time: Yup.string().when("source", {
    is: "Live Data",
    then: (schema) => schema.required(t("SimulationCreationDialog.validation.endTimeRequired", { ns: "simulations" })),
  }),
})

/**
 * Workflow Simulation flow dialog
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} workflowID
 * @param {string} workflowName
 * @return {React.ReactElement}
 */
export function SimulationCreationDialog(props: Readonly<SimulationCreationDialogProps>): React.ReactElement {
  const { open, onClose, workflowID, workflowName } = props

  const { id: projectID } = useParams<{ id: string }>()

  const konanTime = new KonanTimeHelper()

  const MuiTheme = useMuiTheme()
  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationCreationDialog",
  })

  const [activeStep, setActiveStep] = useState<number>(0)

  const createSimulationMutation = useMutation<
    AxiosResponse<WorkflowSimulationJobCreate>,
    AxiosError,
    WorkflowSimulationJobCreateRequest
  >((props: WorkflowSimulationJobCreateRequest) => KonanAPI.createSimulation(props, workflowID), {
    mutationKey: "simulation-create",
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.createSimulation.success"), {
        severity: "success",
      })
      await queryClient.invalidateQueries(["SimulationReports", projectID])

      handleCLoseDialog()
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.createSimulation.failed"), {
        severity: "error",
      })
    },
  })

  const formik = useFormik({
    initialValues: {
      workflowID: workflowID,
      source: "Live Data",
      totalRequestsNumber: 0,
      // check false in case there are no required features
      shouldProceed: true,
      predictions_datafile: "",
      datafile_columns: [""],
      predictions_start_time: konanTime.adjustDate(moment().subtract(29, "days"), "start"),
      predictions_end_time: konanTime.adjustDate(moment(), "end"),
      feedback_column: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await createSimulationMutation.mutateAsync({
        projectId: projectID as string,
        ...(values.source === "Live Data"
          ? {
              predictions_start_time: values.predictions_start_time?.toISOString(),
              predictions_end_time: values.predictions_end_time?.toISOString(),
            }
          : {
              predictions_datafile: values.predictions_datafile,
              feedback_column: ["", null, undefined].includes(values.feedback_column) ? null : values.feedback_column,
            }),
      })

      // TODO:: should navigate to the report just created using search params
      navigate(`/projects/${projectID}/SimulationReports`)
    },
  })

  const isWarningMode = useMemo((): boolean => {
    if (
      ["New Dataset", "Existing Dataset"].includes(formik.values.source) &&
      formik.values.predictions_datafile.length > 0 &&
      !formik.values.shouldProceed
    ) {
      return true
    }

    return false
  }, [formik.values.predictions_datafile.length, formik.values.shouldProceed, formik.values.source])

  // Credit scoring steps with action buttons, description, etc...
  const steps = [
    {
      name: t("steps.dataSource.name"),
      btnText:
        formik.values.source !== "Live Data"
          ? isWarningMode
            ? t("steps.dataSource.buttons.proceedAnyway")
            : t("steps.dataSource.buttons.next")
          : t("steps.dataSource.buttons.simulate"),
      component: (
        <FormikProvider value={formik}>
          <DataSource />
        </FormikProvider>
      ),
      visible: true,
    },
    {
      name: t("steps.feedbackColumn.name"),
      btnText: t("steps.feedbackColumn.simulateButton"),
      component: (
        <InputText
          id="target"
          label={t("steps.feedbackColumn.label")}
          placeholder={t("steps.feedbackColumn.placeholder")}
          value={formik.values.feedback_column ?? ""}
          handleChange={(e) => {
            formik.setFieldValue("feedback_column", e.target.value)
          }}
          handleBlur={formik.handleBlur}
          disabled={formik.isSubmitting}
          key={formik.values.datafile_columns.toString() ?? ""}
          options={formik.values.datafile_columns ?? [""]}
          error={
            formik.touched.feedback_column && Boolean(formik.errors.feedback_column) && formik.errors.feedback_column
          }
          fullWidth
          required
        />
      ),
      visible: formik.values.source !== "Live Data",
    },
  ]

  /**
   * generic click actions function to be called when needed
   * to make it easier to than using switch cases and if conditions
   * @return  {void}
   */
  const clickActions = (): void => {
    switch (activeStep) {
      case 0:
        formik.values.source !== "Live Data" ? setActiveStep(activeStep + 1) : formik.submitForm()
        break
      case 1:
        formik.submitForm()
        break
      default:
        break
    }
  }

  // handles dialog closing
  // 1. closes dialog
  // 2. sets active step to 0 (UX)
  // 3. sets the data source to Live Data (UX)
  const handleCLoseDialog = (): void => {
    onClose()

    setTimeout(() => {
      setActiveStep(0)
      formik.resetForm()
    }, 300)
  }

  const shouldDisableSimulateButton =
    !formik.isValid ||
    createSimulationMutation.isLoading ||
    (formik.values.source === "Live Data" && formik.values.totalRequestsNumber === 0)

  return (
    <Dialog
      open={open}
      onClose={handleCLoseDialog}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { overflow: "hidden" } }}
    >
      <DialogTitle className="dialog-header-base">
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h2-bold">{t("title")}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="a" variantColor={2}>
              {workflowName}
            </Typography>
          </Grid>

          <Grid item xs={12} mt={1.5}>
            <Stepper activeStep={activeStep} connector={null}>
              {steps.map(
                (step, index) =>
                  step.visible && (
                    <Step key={step.name} style={{ paddingLeft: index === 0 ? "0px" : "8px" }}>
                      <StepLabel
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "var(--gray-background-1)",

                            "&.Mui-active": {
                              color: "var(--blue-background-1)",
                            },

                            "&.Mui-completed": {
                              color: "var(--blue-background-1)",
                            },
                          },
                        }}
                      >
                        <Typography
                          variant={index === activeStep ? "h3-bold" : "h3-regular"}
                          variantColor={index === activeStep ? 1 : 2}
                        >
                          {step.name}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ),
              )}
            </Stepper>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent className={styles.flowContainer}>
        <Grid container item xs={12}>
          {steps[activeStep]?.component}
        </Grid>
      </DialogContent>

      <DialogActions className="dialog-actions-base">
        <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
          <Box display={"flex"}>
            {activeStep === 1 && (
              <Button
                variant="secondary"
                onClick={() => {
                  setActiveStep(activeStep - 1)
                }}
                id="retest"
                disabled={createSimulationMutation.isLoading}
              >
                {t("buttons.back")}
              </Button>
            )}
          </Box>

          <Box display={"flex"}>
            <Box mr={1}>
              <Button onClick={handleCLoseDialog} disabled={createSimulationMutation.isLoading} variant="secondary">
                {t("buttons.cancel")}
              </Button>
            </Box>

            <Box>
              <Button
                onClick={clickActions}
                disabled={shouldDisableSimulateButton}
                variant="primary"
                className={isWarningMode ? styles.nextBtn : ""}
                tooltip={
                  isWarningMode
                    ? t("tooltips.missingRequiredFeatures")
                    : formik.values.source === "Live Data" && formik.values.totalRequestsNumber === 0
                      ? t("tooltips.noRequestsSent")
                      : ""
                }
                tooltipPlacement="top"
                isLoading={formik.isSubmitting}
              >
                {steps[activeStep]?.btnText}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
